"use client";

import { curveLinear } from "@visx/curve";
import { Area, AreaClosed, LinePath } from "@visx/shape";
import { motion } from "framer-motion";
import React, { useId } from "react";
import { useChartContext } from "../../../context/ChartContext";
import { BaseDataPoint } from "../../../utils/charts";
import { splitOnGaps } from "../../../utils/splitOnGaps";

export type AreaSeriesBaseline = number | ((point: BaseDataPoint, index: number) => number);

interface AreaSeriesProps {
  /**
   * The points to draw.
   *
   * Optional ONLY inside a streaming chart, where the points do not exist until
   * the reader looks at a window: there the child names its stream with
   * `seriesKey` and the chart supplies `data` before this component renders. In
   * an ordinary chart it is required in practice — a series with neither is
   * skipped rather than drawn.
   */
  data?: BaseDataPoint[];
  /**
   * Which stream this series draws, in a chart with a streaming `source`.
   *
   * Defaults to `"line"`, the primary stream. Ignored entirely by a chart that
   * holds its own data, so the same `<AreaSeries>` serves both sources.
   */
  seriesKey?: string;
  /**
   * Relabel this series once its points arrive, in a streaming chart.
   *
   * Declared here rather than only on the chart because the element carrying it
   * is this one; the streaming branch reads it, resolves it against every
   * assembled stream, and passes the result down as `label`. This component
   * never calls it — by the time it renders, `label` is already the answer.
   */
  labelFromData?: (
    plotted: BaseDataPoint[],
    series: import("../../../utils/timeSeriesTiles").AssembledSeries
  ) => string;
  color?: string;
  label?: string;
  /** Position in a tiled chart's legend, lowest first; series without one follow in draw order. */
  legendOrder?: number;
  /** Streaming primary plot only: an optional stream, dropped where the window has none of it, named where it fills only part. */
  partialCoverageLabel?: string;
  categoryColors?: Record<string, string>;
  categoryLabels?: Record<string, string>;
  /**
   * Optional baseline for stacked/banded areas. When omitted, AreaSeries
   * preserves its historical behavior and closes the filled area to the chart
   * baseline. When provided, the fill renders between `baseline` and `yValue`
   * for each point while the visible line still follows `yValue`.
   *
   * A constant `number` or a per-point function accessor — typically
   * `baselineFromPoint`, reading each point's `baselineValue`. The per-point
   * form is required for banded areas in zoomable charts: the floor must travel
   * with its point so it survives `ChartContainer`'s zoom-filtering.
   *
   * Read the baseline from the point, not the `index`: with `maxGapMs` set, the
   * `index` reflects the gap-split array (null break-markers inserted), so it no
   * longer maps to the original data index for external lookups.
   */
  baseline?: AreaSeriesBaseline;
  /**
   * When true (and `baseline` is set), the tooltip renders this series as a
   * `baseline–yValue` range instead of just `yValue` — for min–max bands.
   * Stacked areas leave this off so their tooltips keep showing a single value.
   */
  tooltipRange?: boolean;
  /** Fill opacity at the strongest point of the default gradient. */
  fillOpacity?: number;
  /** Whether to draw the outline path on top of the filled area. */
  showLine?: boolean;
  /**
   * When set, break the area (and outline) wherever consecutive points are
   * spaced more than this many ms apart (missing buckets / outages). Omit to
   * fill across gaps.
   */
  maxGapMs?: number;
  animate?: boolean;
}

/**
 * AreaSeries
 *
 * Chart component for rendering area charts with gradient fill.
 * Displays data as a filled area with optional line path, supporting animations and custom colors.
 */
export const AreaSeries: React.FC<AreaSeriesProps> = ({
  data,
  color = "var(--color-viz-default)",
  baseline,
  fillOpacity = 0.1,
  showLine = true,
  maxGapMs,
  animate = true,
}) => {
  const uniqueGradientId = useId();
  const clipPathId = useId();
  const { xScale, yScale, animationSettings, entrance } = useChartContext();
  // Only the chart's first commit animates; a legend toggle re-mounts the series
  // and must not replay the grow-in.
  // Latched at mount, not recomputed per render: the flag flips in a passive effect,
  // and the chart re-renders while the entrance is still running — tooltip state
  // lives in this same context, so a hover does it. Recomputing would swap the
  // wrapper out mid-animation and snap the geometry to full scale.
  // The self-clip follows it — the clip exists only to contain the scaleY grow.
  const [firstCommit] = React.useState(() => entrance?.played !== true);
  const playEntrance = animate && firstCommit;

  const xRange = xScale.range();
  const yRange = yScale.range();

  const getBaselineValue = (point: BaseDataPoint, index: number) =>
    typeof baseline === "function" ? baseline(point, index) : (baseline ?? 0);

  const getDrawYValue = (point: BaseDataPoint) => point.chartYValue ?? point.yValue;
  // `data` is absent only for a streaming child that a `ChartContainer` has not
  // filled yet — a frame before the first slice lands, or a stream this source
  // does not produce. Draw nothing rather than throwing; the chart around it is
  // still correct.
  const resolvedData = data ?? [];
  const points = maxGapMs != null ? splitOnGaps(resolvedData, maxGapMs) : resolvedData;

  // Camera mode (animate=false) rides an ancestor's per-frame scaleX pan/zoom
  // transform; non-scaling-stroke holds the outline at a constant width instead
  // of stretching with the geometry. Left off while the scaleY entrance animates.
  const strokeVectorEffect = animate ? undefined : "non-scaling-stroke";

  const gStyle = {
    transformOrigin: `0 ${yRange[0]}px`,
    transformBox: "fill-box" as const,
    isolation: "isolate" as const,
  };

  const innerContent = (
    <>
      {baseline === undefined ? (
        <AreaClosed
          data={points}
          x={(d) => (d ? xScale(d.xValue) : 0)}
          y={(d) => (d ? yScale(getDrawYValue(d)) : 0)}
          yScale={yScale}
          defined={(d) => d != null}
          fill={`url(#${uniqueGradientId})`}
          curve={curveLinear}
        />
      ) : (
        <Area
          data={points}
          x={(d) => (d ? xScale(d.xValue) : 0)}
          y0={(d, index) => (d ? yScale(getBaselineValue(d, index)) : 0)}
          y1={(d) => (d ? yScale(getDrawYValue(d)) : 0)}
          defined={(d) => d != null}
          fill={color}
          fillOpacity={fillOpacity}
          curve={curveLinear}
        />
      )}
      {showLine ? (
        <LinePath
          data={points}
          x={(d) => (d ? xScale(d.xValue) : 0)}
          y={(d) => (d ? yScale(getDrawYValue(d)) : 0)}
          defined={(d) => d != null}
          stroke={color}
          strokeWidth={3}
          vectorEffect={strokeVectorEffect}
          curve={curveLinear}
        />
      ) : null}
    </>
  );

  // The self-clip exists only to contain the scaleY grow animation; its rect is
  // pinned to the static plot-area range, so under a live ancestor transform
  // (TiledTimeSeriesChart's pan) the area would be clipped against a stale
  // rectangle while it slides. When not animating, drop it and let the content
  // ride the ancestor transform — the camera chart supplies its own plot clip.
  return (
    <>
      <defs>
        <linearGradient id={uniqueGradientId} x1="0%" y1="0%" x2="0%" y2="100%">
          <stop offset="50%" stopColor={color} stopOpacity={fillOpacity} />
          <stop offset="100%" stopColor={color} stopOpacity={0} />
        </linearGradient>
        {playEntrance && (
          <clipPath id={clipPathId}>
            <rect x={xRange[0]} y={yRange[1]} width={xRange[1] - xRange[0]} height={yRange[0] - yRange[1]} />
          </clipPath>
        )}
      </defs>

      {playEntrance ? (
        <g clipPath={`url(#${clipPathId})`}>
          <motion.g
            initial={{ scaleY: 0 }}
            animate={{ scaleY: 1 }}
            transition={{
              duration: animationSettings.duration,
              ease: animationSettings.ease as "easeInOut",
            }}
            style={gStyle}
          >
            {innerContent}
          </motion.g>
        </g>
      ) : (
        <g style={gStyle}>{innerContent}</g>
      )}
    </>
  );
};
